import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const LOGO = "/assets/kreyola-logo.webp";

function Shell({ title, children }) {
  const { t } = useLang();
  return (
    <div className="grid min-h-[calc(100vh-140px)] lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src="https://customer-assets-eiarnc6j.emergentagent.net/job_kreyola-boutik/artifacts/pdh3b8ei_REAL%20sunsetpalm-tree.webp" alt="Lakay" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy/50" />
        <div className="absolute bottom-12 left-12 right-12 text-cream">
          <p className="font-script text-4xl text-gold">Lakay toujou nan kè nou</p>
          <p className="mt-3 text-sm text-cream/80">{t("auth.tagline")}</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="mb-6 flex flex-col items-center text-center">
            <img src={LOGO} alt="Kreyola Boutik" className="h-16 w-16 object-contain" />
            <h1 className="mt-4 font-display text-3xl font-bold text-navy">{title}</h1>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-border bg-cream-card px-4 py-3 text-sm outline-none transition focus:border-gold";

export function Login() {
  const { t, lang } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast.success(t("auth.welcomeBack"));
      navigate(u.role === "admin" ? "/admin" : (location.state?.from || "/account"));
    } catch (e) { setErr(apiError(e.response?.data?.detail, lang) || e.message); }
    finally { setLoading(false); }
  };
  return (
    <Shell title={t("auth.loginTitle")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {err && <p className="rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive" data-testid="login-error">{err}</p>}
        <input type="email" required placeholder={t("common.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="login-email" />
        <input type="password" required placeholder={t("common.password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="login-password" />
        <Link to="/forgot-password" className="block text-right text-sm text-sunset hover:underline">{t("auth.forgot")}</Link>
        <button type="submit" disabled={loading} className="btn-gold w-full rounded-full py-3 text-sm font-bold" data-testid="login-submit">{loading ? "..." : t("nav.login")}</button>
        <p className="text-center text-sm text-muted-foreground">{t("auth.noAccount")} <Link to="/register" className="font-semibold text-sunset hover:underline">{t("nav.register")}</Link></p>
      </form>
    </Shell>
  );
}

export function Register() {
  const { t, lang } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await register(form.name, form.email, form.password); toast.success(t("auth.accountCreated")); navigate("/account"); }
    catch (e) { setErr(apiError(e.response?.data?.detail, lang) || e.message); }
    finally { setLoading(false); }
  };
  return (
    <Shell title={t("auth.registerTitle")}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {err && <p className="rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive" data-testid="register-error">{err}</p>}
        <input required placeholder={t("common.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="register-name" />
        <input type="email" required placeholder={t("common.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="register-email" />
        <input type="password" required minLength={6} placeholder={t("common.password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="register-password" />
        <button type="submit" disabled={loading} className="btn-gold w-full rounded-full py-3 text-sm font-bold" data-testid="register-submit">{loading ? "..." : t("nav.register")}</button>
        <p className="text-center text-sm text-muted-foreground">{t("auth.haveAccount")} <Link to="/login" className="font-semibold text-sunset hover:underline">{t("nav.login")}</Link></p>
      </form>
    </Shell>
  );
}

export function ForgotPassword() {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch { setSent(true); }
  };
  return (
    <Shell title={t("auth.forgot")}>
      {sent ? (
        <p className="rounded-xl bg-palm-light/50 p-4 text-center text-sm text-palm" data-testid="forgot-sent">{lang === "fr" ? "Si l'email existe, un lien de réinitialisation a été envoyé." : "If the email exists, a reset link has been sent."}</p>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <input type="email" required placeholder={t("common.email")} value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="forgot-email" />
          <button type="submit" className="btn-gold w-full rounded-full py-3 text-sm font-bold" data-testid="forgot-submit">{t("common.submit")}</button>
          <p className="text-center text-sm"><Link to="/login" className="text-sunset hover:underline">{t("nav.login")}</Link></p>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const token = new URLSearchParams(useLocation().search).get("token") || "";
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/auth/reset-password", { token, password }); toast.success(lang === "fr" ? "Mot de passe réinitialisé !" : "Password reset!"); navigate("/login"); }
    catch (e) { setErr(apiError(e.response?.data?.detail, lang)); }
  };
  return (
    <Shell title={t("auth.resetTitle")}>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        {err && <p className="rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{err}</p>}
        <input type="password" required minLength={6} placeholder={t("common.password")} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="reset-password" />
        <button type="submit" className="btn-gold w-full rounded-full py-3 text-sm font-bold" data-testid="reset-submit">{t("common.save")}</button>
      </form>
    </Shell>
  );
}
